import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Link, useSearchParams } from "react-router-dom"
import { useQuery } from "@tanstack/react-query"
import { CircularProgress } from "@mui/material"
import { sageJobSiteService } from "../../services/sageJobSiteService"
import SignInLocationCell from "../construction/SignInLocationCell"
import { t } from "../../i18n"
import { SageHoursScope, SageJobSite, SageJobSiteHoursResponse } from "../../Interfaces"
import { useSiteTerminology } from "../../contexts/PublicSiteContext"
import {
  costCodeDisplayName,
  emptyHoursMessage,
  employeeDisplayName,
  formatHours,
  HOURS_SUBTITLE,
  isHoursEmpty,
  parseHoursScope,
} from "../../utils/jobSiteDailyHours"
import {
  addMonths,
  endOfMonthISO,
  initialHoursDaysRange,
  isISODate,
  monthWindowISO,
  parseISODateLocal,
  startOfMonthISO,
  todayISODate,
  unionIsoRange,
} from "../../utils/isoDate"
import {
  hasHoursOnDay,
  nextHoursDay,
  previousHoursDay,
  snapToHoursDay,
} from "../../utils/jobSiteHoursDays"
import JobSiteHoursCalendar from "./JobSiteHoursCalendar"

function HoursChip({
  label,
  hours,
  filled = false,
}: {
  label: string
  hours: number
  filled?: boolean
}) {
  return (
    <span
      className={
        filled
          ? "inline-flex items-center rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white"
          : "inline-flex items-center rounded-full border-2 border-primary bg-white px-4 py-2 text-sm font-semibold text-primary"
      }
    >
      {label} {formatHours(hours)} hours
    </span>
  )
}

function HoursPane({
  title,
  nameHeader,
  rows,
  totalHours,
}: {
  title: string
  nameHeader: string
  rows: Array<{ key: string | number, name: string, hours: number, muted?: boolean }>
  totalHours: number
}) {
  return (
    <section className="bg-white shadow-lg rounded-2xl overflow-hidden flex flex-col min-h-[280px]">
      <h3 className="text-lg font-semibold px-5 pt-5 pb-3">{title}</h3>
      <table className="w-full text-sm text-start flex-1">
        <thead>
          <tr className="bg-secondary-extra-light text-primary-dark">
            <th className="px-5 py-2 text-start font-medium">{nameHeader}</th>
            <th className="px-5 py-2 text-end font-medium">Hours</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key} className="border-t border-gray-100">
              <td className={`px-5 py-2.5 ${row.muted ? "italic text-gray-600" : ""}`}>
                {row.name}
              </td>
              <td className="px-5 py-2.5 text-end font-semibold tabular-nums">
                {formatHours(row.hours)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="mt-auto bg-secondary-extra-light px-5 py-3 flex justify-between items-center font-semibold">
        <span>Total</span>
        <span className="tabular-nums">{formatHours(totalHours)}</span>
      </div>
    </section>
  )
}

function ScopeToggle({
  scope,
  onScopeChange,
}: {
  scope: SageHoursScope
  onScopeChange: (scope: SageHoursScope) => void
}) {
  return (
    <div
      className="inline-flex rounded-full border border-gray-200 bg-white p-1 shadow-sm"
      role="radiogroup"
      aria-label="Hours scope"
    >
      {(["day", "project"] as SageHoursScope[]).map((value) => {
        const selected = scope === value
        return (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onScopeChange(value)}
            className={
              selected
                ? "px-4 py-1.5 rounded-full text-sm font-semibold bg-primary text-white"
                : "px-4 py-1.5 rounded-full text-sm font-semibold text-primary hover:bg-secondary-extra-light"
            }
          >
            {value === "day" ? "Day" : "Project"}
          </button>
        )
      })}
    </div>
  )
}

export function JobSiteDailyHoursView({
  siteName,
  date,
  scope = "day",
  onScopeChange,
  onPreviousDay,
  onNextDay,
  onDateChange,
  onMonthChange,
  enabledDates,
  canGoPrevious = true,
  canGoNext = true,
  summary,
  isLoading,
  error,
}: {
  siteName: string
  date: string
  scope?: SageHoursScope
  onScopeChange?: (scope: SageHoursScope) => void
  onPreviousDay: () => void
  onNextDay: () => void
  onDateChange: (next: string) => void
  onMonthChange?: (year: number, monthIndex: number) => void
  enabledDates?: Iterable<string>
  canGoPrevious?: boolean
  canGoNext?: boolean
  summary?: SageJobSiteHoursResponse | null
  isLoading?: boolean
  error?: string | null
}) {
  const empty = !isLoading && !error && (!summary || isHoursEmpty(summary))
  const totalHours = summary?.totalHours ?? 0
  const projectMode = scope === "project"
  const totalLabel = projectMode ? "Project total" : "Day total"

  const employeeRows = useMemo(
    () => (summary?.byEmployee || []).map((row) => ({
      key: row.employeeId,
      name: employeeDisplayName(row),
      hours: row.hours,
    })),
    [summary],
  )

  const costCodeRows = useMemo(
    () => (summary?.byCostCode || []).map((row) => ({
      key: row.unassigned ? "unassigned" : row.costCodeId ?? costCodeDisplayName(row),
      name: costCodeDisplayName(row),
      hours: row.hours,
      muted: Boolean(row.unassigned),
    })),
    [summary],
  )

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
        <div>
          <h2 className="text-2xl md:text-3xl font-bold">{siteName}</h2>
          <p className="text-gray-600 mt-1">{HOURS_SUBTITLE}</p>
        </div>
        <ScopeToggle scope={scope} onScopeChange={onScopeChange || (() => undefined)} />
      </div>

      <div className="flex flex-wrap items-center gap-3 pb-5 mb-5 border-b border-gray-200">
        <JobSiteHoursCalendar
          date={date}
          disabled={projectMode}
          enabledDates={enabledDates}
          canGoPrevious={canGoPrevious}
          canGoNext={canGoNext}
          onPreviousDay={onPreviousDay}
          onNextDay={onNextDay}
          onDateChange={onDateChange}
          onMonthChange={onMonthChange}
        />
        <HoursChip label={totalLabel} hours={totalHours} filled />
      </div>

      {isLoading && (
        <div className="w-full flex justify-center items-center h-48 bg-white shadow-lg rounded-2xl">
          <CircularProgress />
        </div>
      )}

      {error && !isLoading && (
        <p className="bg-white shadow-lg rounded-2xl px-5 py-8 text-center text-red-600">{error}</p>
      )}

      {empty && (
        <p className="bg-white shadow-lg rounded-2xl px-5 py-10 text-center text-gray-600">
          {emptyHoursMessage(scope)}
        </p>
      )}

      {!isLoading && !error && summary && !empty && (
        <div className="grid gap-4 md:grid-cols-2">
          <HoursPane
            title="Hours by employee"
            nameHeader="Employee"
            rows={employeeRows}
            totalHours={totalHours}
          />
          <HoursPane
            title="Hours by cost code"
            nameHeader="Cost code"
            rows={costCodeRows}
            totalHours={totalHours}
          />
        </div>
      )}
    </div>
  )
}

export default function JobSiteDailyHours({ jobSite }: { jobSite: SageJobSite }) {
  const [searchParams, setSearchParams] = useSearchParams()
  const dateParam = searchParams.get("date")
  const date = isISODate(dateParam) ? dateParam : todayISODate()
  const scope = parseHoursScope(searchParams.get("scope"))
  const [daysRange, setDaysRange] = useState(() => initialHoursDaysRange(date))
  const [triedExpandBefore, setTriedExpandBefore] = useState(false)
  const [triedExpandAfter, setTriedExpandAfter] = useState(false)
  const pendingNav = useRef<"prev" | "next" | null>(null)

  const updateParams = useCallback((next: { date?: string, scope?: SageHoursScope }) => {
    setSearchParams((current) => {
      const nextParams = new URLSearchParams(current)
      const currentDate = current.get("date")
      const nextDate = next.date ?? (isISODate(currentDate) ? currentDate : todayISODate())
      const nextScope = next.scope ?? parseHoursScope(current.get("scope"))
      if (nextScope === "project") {
        nextParams.set("scope", "project")
      } else {
        nextParams.delete("scope")
      }
      if (nextDate === todayISODate()) {
        nextParams.delete("date")
      } else {
        nextParams.set("date", nextDate)
      }
      return nextParams
    }, { replace: true })
  }, [setSearchParams])

  const setDate = useCallback((next: string) => {
    if (!isISODate(next)) return
    updateParams({ date: next, scope: "day" })
  }, [updateParams])

  const expandDaysRange = useCallback((year: number, monthIndex: number) => {
    const window = monthWindowISO(year, monthIndex, 1, 1)
    setDaysRange((current) => {
      const next = unionIsoRange(current, window)
      if (next.from === current.from && next.to === current.to) return current
      return next
    })
  }, [])

  const { data: daysResponse, isFetching: daysFetching } = useQuery(
    ["sage-job-site-hours-days", jobSite.id, daysRange.from, daysRange.to],
    () => sageJobSiteService.getHoursDays(jobSite.id, daysRange),
    {
      enabled: !!jobSite.id && scope === "day",
      keepPreviousData: true,
      suspense: false,
      useErrorBoundary: false,
    },
  )

  const hoursDays = useMemo(() => daysResponse?.dates ?? [], [daysResponse])
  const daysReady = daysResponse != null
  const previousDay = previousHoursDay(hoursDays, date)
  const nextDay = nextHoursDay(hoursDays, date)
  const adjacentMonthLoaded = (delta: number) => {
    const current = parseISODateLocal(date)
    const month = addMonths(current.getFullYear(), current.getMonth(), delta)
    const from = startOfMonthISO(month.year, month.monthIndex)
    const to = endOfMonthISO(month.year, month.monthIndex)
    return daysRange.from <= from && daysRange.to >= to
  }
  const canExpandBefore = daysReady && hoursDays.length > 0 && previousDay == null
    && !triedExpandBefore && !adjacentMonthLoaded(-1)
  const canExpandAfter = daysReady && hoursDays.length > 0 && nextDay == null
    && !triedExpandAfter && !adjacentMonthLoaded(1)

  useEffect(() => {
    if (scope !== "day" || !daysReady) return
    if (hasHoursOnDay(hoursDays, date)) return
    const snapped = snapToHoursDay(hoursDays, date)
    if (snapped && snapped !== date) setDate(snapped)
  }, [scope, daysReady, hoursDays, date, setDate])

  useEffect(() => {
    if (!pendingNav.current || daysFetching || !daysReady) return
    const direction = pendingNav.current
    pendingNav.current = null
    const target = direction === "prev"
      ? previousHoursDay(hoursDays, date)
      : nextHoursDay(hoursDays, date)
    if (target) setDate(target)
  }, [daysFetching, daysReady, hoursDays, date, setDate])

  const goPreviousDay = () => {
    if (previousDay) {
      setDate(previousDay)
      return
    }
    if (!canExpandBefore) return
    const edge = parseISODateLocal(daysRange.from)
    const prevMonth = addMonths(edge.getFullYear(), edge.getMonth(), -1)
    const nextRange = unionIsoRange(daysRange, monthWindowISO(prevMonth.year, prevMonth.monthIndex, 0, 0))
    pendingNav.current = "prev"
    setTriedExpandBefore(true)
    if (nextRange.from === daysRange.from && nextRange.to === daysRange.to) return
    setDaysRange(nextRange)
  }

  const goNextDay = () => {
    if (nextDay) {
      setDate(nextDay)
      return
    }
    if (!canExpandAfter) return
    const edge = parseISODateLocal(daysRange.to)
    const nextMonth = addMonths(edge.getFullYear(), edge.getMonth(), 1)
    const nextRange = unionIsoRange(daysRange, monthWindowISO(nextMonth.year, nextMonth.monthIndex, 0, 0))
    pendingNav.current = "next"
    setTriedExpandAfter(true)
    if (nextRange.from === daysRange.from && nextRange.to === daysRange.to) return
    setDaysRange(nextRange)
  }

  const hoursDate = scope === "day" && daysReady
    ? (hasHoursOnDay(hoursDays, date) ? date : snapToHoursDay(hoursDays, date) ?? date)
    : date

  const { data: summary, isLoading, error } = useQuery(
    ["sage-job-site-hours", jobSite.id, scope, scope === "project" ? "project" : hoursDate],
    () => sageJobSiteService.getHours(jobSite.id, {
      scope,
      date: scope === "day" && isISODate(hoursDate) ? hoursDate : undefined,
    }),
    {
      enabled: !!jobSite.id && (scope === "project" || daysReady),
      keepPreviousData: true,
      suspense: false,
      useErrorBoundary: false,
    },
  )

  return (
    <>
    <JobSiteDailyHoursView
      siteName={jobSite.name}
      date={date}
      scope={scope}
      onScopeChange={(next) => updateParams({ scope: next })}
      onPreviousDay={goPreviousDay}
      onNextDay={goNextDay}
      onDateChange={setDate}
      onMonthChange={expandDaysRange}
      enabledDates={scope === "day" && daysReady ? hoursDays : undefined}
      canGoPrevious={scope === "day" && daysReady && (previousDay != null || canExpandBefore)}
      canGoNext={scope === "day" && daysReady && (nextDay != null || canExpandAfter)}
      summary={summary}
      isLoading={isLoading || (scope === "day" && !daysReady)}
      error={error instanceof Error ? error.message : error ? String(error) : null}
    />
    {scope === "day" && daysReady && isISODate(hoursDate) ? (
      <JobSiteSignInLocations jobSiteId={jobSite.id} date={hoursDate} />
    ) : null}
    </>
  )
}

/** Where each person was when they clocked in on this day (captured by the app). */
export function JobSiteSignInLocations({ jobSiteId, date }: { jobSiteId: number, date: string }) {
  const { data, isLoading, error } = useQuery(
    ["sage-job-site-sign-ins", jobSiteId, date],
    () => sageJobSiteService.getWorkDays(jobSiteId, date),
    { enabled: !!jobSiteId, suspense: false, useErrorBoundary: false },
  )
  if (isLoading || error) return null
  const days = (data?.data ?? []).filter((d) => d.clockedInAt)
  return (
    <section className="bg-white shadow-lg rounded-2xl overflow-hidden mt-6" data-testid="sign-in-locations">
      <h3 className="text-lg font-semibold px-5 pt-5 pb-3">{t("signInLocation.title")}</h3>
      {days.length === 0 ? (
        <p className="px-5 pb-5 text-gray-600 font-source">{t("signInLocation.empty")}</p>
      ) : (
        <table className="w-full text-sm text-start">
          <tbody>
            {days.map((d, i) => (
              <tr key={d.id ?? i} className="border-t border-gray-100">
                <td className="px-5 py-2.5">{[d.employee?.firstName, d.employee?.lastName].filter(Boolean).join(" ")}</td>
                <td className="px-5 py-2.5 tabular-nums">
                  {d.clockedInAt ? new Date(d.clockedInAt).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : ""}
                </td>
                <td className="px-5 py-2.5"><SignInLocationCell source={d} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  )
}

export function JobSiteBackLink() {
  const { locationsLabel } = useSiteTerminology()
  return (
    <div className="flex justify-start mb-4">
      <Link to="/app/locations" className="text-primary underline">
        Back to {locationsLabel}
      </Link>
    </div>
  )
}
